import { useState } from "react";
import { Button, TextArea, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiApi, type AiFeedbackSurface } from "../../../api/aiApi";
import "./aiFeedback.css";

/**
 * 👍 / 👎 under an AI answer (ADR-0035, like Turbo's AiFeedback): up is saved at once, down asks
 * "what was wrong?" first. For the assistant the server takes the question / answer from the
 * stored conversation; other surfaces pass them in.
 */
export function AiFeedbackButtons({
  surface,
  refKey,
  initial,
  question,
  answer,
}: {
  surface: AiFeedbackSurface;
  refKey: string;
  initial?: "up" | "down" | null;
  question?: string | null;
  answer?: string | null;
}) {
  const toast = useCkToast();
  const [rating, setRating] = useState<"up" | "down" | null>(initial ?? null);
  const [asking, setAsking] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (value: "up" | "down", text?: string) => {
    setBusy(true);
    try {
      await aiApi.feedback({
        surface,
        refKey,
        rating: value,
        comment: text?.trim() || null,
        question: question ?? null,
        answer: answer ?? null,
        pagePath: `${window.location.pathname}${window.location.search}`,
      });
      setRating(value);
      setAsking(false);
      toast.success(value === "up" ? "תודה על המשוב" : "תודה — נשתמש בזה כדי להשתפר");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "המשוב לא נשמר");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-fb">
      <button
        type="button"
        className={`ai-fb__btn${rating === "up" ? " is-on" : ""}`}
        aria-label="תשובה טובה"
        title="תשובה טובה"
        aria-pressed={rating === "up"}
        disabled={busy}
        onClick={() => void send("up")}
      >
        👍
      </button>
      <button
        type="button"
        className={`ai-fb__btn${rating === "down" || asking ? " is-on" : ""}`}
        aria-label="תשובה לא טובה"
        title="תשובה לא טובה"
        aria-pressed={rating === "down"}
        disabled={busy}
        onClick={() => setAsking((v) => !v)}
      >
        👎
      </button>
      {asking ? (
        <div className="ai-fb__ask">
          <TextArea rows={2} value={comment} maxLength={2000} placeholder="מה לא היה טוב? (לא חובה)" onChange={(e) => setComment(e.target.value)} />
          <div className="ai-fb__btns">
            <Button size="sm" variant="primary" disabled={busy} onClick={() => void send("down", comment)}>
              שליחה
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAsking(false)}>
              ביטול
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
